/**
 * Auditor / compliance view — FDA-grade immutable access log.
 *
 * Backend: GET /viewer-api/audit/_search (postgres-backed, hash-
 * chained, see auditLog.ts). Rows match the DB schema (0008
 * migration). Every row carries prev_hash + row_hash so the UI
 * can surface the chain integrity status at the top bar via
 * /viewer-api/audit/_verify.
 *
 * Regulatory mapping:
 *   21 CFR Part 11 §11.10(e) — append-only audit trail with actor
 *     identity, UTC timestamp, event type, record reference, old/
 *     new values (stored in metadata.before / metadata.after).
 *   HIPAA §164.312(b)       — audit controls over PHI access.
 *   FDA cybersecurity (2023) — session + failed-auth visibility.
 *
 * Export: two formats. CSV (RFC 4180) for spreadsheet triage, and
 * signed JSON evidence bundle (rows + chain-verify output + SHA256
 * of the bundle) for regulatory submissions. Both are generated
 * client-side from fetched rows — no additional server round trip.
 */
import { createEffect, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

const PAGE_SIZE_OPTIONS = [50, 100, 250, 500] as const;
const DEFAULT_PAGE_SIZE = 100;

interface AuditRow {
  id:          number;
  occurred_at: string;
  kind:        string;
  object_ref:  string;
  actor_sub:   string | null;
  actor_role:  string | null;
  ip:          string | null;
  user_agent:  string | null;
  patient_mrn: string | null;
  study_uid:   string | null;
  metadata:    Record<string, any>;
  prev_hash:   string;
  row_hash:    string;
}

interface ChainStatus { total: number; broken: number[]; missing_prev: number[]; }

/** Bearer-token from session storage — matches the pattern used by every
 *  other /viewer-api/admin/* fetch in the viewer.  Without it the
 *  middleware in viewer/src/server.ts replies 401 and the page renders
 *  empty (no error surface, just zero rows). */
function authHeaders(): Record<string, string> {
  const auth = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  return { Authorization: `Bearer ${auth?.accessToken ?? ""}` };
}
async function fetchAudit(filters: Record<string, string>): Promise<AuditRow[]> {
  const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const params = new URLSearchParams(clean);
  const rsp = await fetch(`/viewer-api/audit/_search?${params}`, { headers: authHeaders() });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchChain(): Promise<ChainStatus> {
  const rsp = await fetch(`/viewer-api/audit/_verify`, { headers: authHeaders() });
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

const EVENT_KINDS = [
  { value: "",                 label: "all events" },
  { value: "auth.",            label: "authentication (auth.*)" },
  { value: "study.",           label: "study / PHI access (study.*)" },
  { value: "path_report.",     label: "pathology reports" },
  { value: "endo_report.",     label: "endoscopy reports" },
  { value: "report.",          label: "report export" },
  { value: "share.",           label: "patient share" },
  { value: "qa.",              label: "technologist QA" },
  { value: "user.",            label: "user administration" },
  { value: "config.",          label: "configuration changes" },
];

export function AuditorPage() {
  const [user,    setUser]    = createSignal("");
  const [patient, setPatient] = createSignal("");
  const [study,   setStudy]   = createSignal("");
  const [kind,    setKind]    = createSignal("");
  const [from,    setFrom]    = createSignal("");
  const [to,      setTo]      = createSignal("");
  const [pageSize, setPageSize] = createSignal<number>(DEFAULT_PAGE_SIZE);
  const [page,    setPage]    = createSignal(1);

  // Reset paging to page 1 whenever any filter changes — otherwise
  // a user on page 5 of "all events" who narrows to a single MRN
  // would see the now-too-short result set's page 5 (i.e. blank).
  createEffect(() => {
    user(); patient(); study(); kind(); from(); to(); pageSize();
    setPage(1);
  });

  const rows = createQuery(() => ({
    queryKey: ["audit", user(), patient(), study(), kind(), from(), to(), pageSize(), page()],
    queryFn:  () => fetchAudit({
      user: user(), patient: patient(), study: study(), kind: kind(),
      from: from() ? from() + "T00:00:00Z" : "",
      to:   to()   ? to()   + "T23:59:59Z" : "",
      limit:  String(pageSize()),
      offset: String((page() - 1) * pageSize()),
    }),
    refetchInterval: 15_000,
    keepPreviousData: true,
  }));
  // A short page is the last page (no need to wire up a count query
  // — server clamps `limit` so a partial page reliably indicates
  // end-of-results).
  const isLastPage = () => (rows.data?.length ?? 0) < pageSize();
  const chain = createQuery(() => ({
    queryKey: ["audit-chain"],
    queryFn:  fetchChain,
    refetchInterval: 60_000,
  }));

  const tamperedCount = () => (chain.data?.broken.length ?? 0) + (chain.data?.missing_prev.length ?? 0);
  const chainOk = () => chain.data && tamperedCount() === 0;

  return (
    <div class="flex h-full flex-col">
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Audit log</span>
        <span class="text-xs text-ink-700">
          21 CFR Part 11 §11.10(e) · HIPAA §164.312(b) — append-only, hash-chained, tamper-evident.
        </span>
        <Show when={chain.data}>
          <Show when={chainOk()} fallback={
            <span class="chip-danger" title={`broken ids: ${chain.data!.broken.join(", ")}`}>
              CHAIN BROKEN — {tamperedCount()} row(s)
            </span>
          }>
            <span class="chip-ok">chain verified ({chain.data!.total})</span>
          </Show>
        </Show>
        <span class="ml-auto text-[11px] text-ink-700">
          {rows.data?.length ?? 0} events shown
        </span>
      </header>

      <div class="flex flex-wrap items-end gap-2 border-b border-ink-300 bg-ink-100 px-4 py-2">
        <Filter label="Actor (subject)" value={user()}    onInput={setUser}    placeholder="e.g. user-id…" />
        <Filter label="Patient MRN"         value={patient()} onInput={setPatient} placeholder="MRN-…" />
        <Filter label="Study UID"           value={study()}   onInput={setStudy}   placeholder="1.2.826…" />
        <label class="flex flex-col gap-0.5 text-[10px] text-ink-700">
          <span>Event kind</span>
          <select class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
                  value={kind()} onChange={(e) => setKind(e.currentTarget.value)}>
            <For each={EVENT_KINDS}>{(k) => <option value={k.value}>{k.label}</option>}</For>
          </select>
        </label>
        <Filter label="From" value={from()} onInput={setFrom} placeholder="YYYY-MM-DD" />
        <Filter label="To"   value={to()}   onInput={setTo}   placeholder="YYYY-MM-DD" />
        <button class="btn-ghost text-xs" onClick={() => exportCSV(rows.data ?? [])}>CSV</button>
        <button class="btn-ghost text-xs"
                onClick={() => exportEvidence(rows.data ?? [], chain.data ?? null)}>
          Evidence bundle (JSON)
        </button>
      </div>

      {/* Pagination bar — visible whenever there are rows. Page size
       *  is per-session; resets to page 1 if filters or page size
       *  change so a narrowed search never silently lands on a blank
       *  out-of-range page.
       */}
      <div class="flex flex-wrap items-center gap-2 border-b border-ink-300 bg-ink-100 px-4 py-2 text-[11px] text-ink-700">
        <label class="flex items-center gap-1">
          <span>Page size</span>
          <select class="rounded border border-ink-300 bg-ink-200 px-1.5 py-0.5 font-mono text-[11px]"
                  value={String(pageSize())}
                  onChange={(e) => setPageSize(parseInt(e.currentTarget.value, 10))}>
            <For each={PAGE_SIZE_OPTIONS}>{(n) => <option value={String(n)}>{n}</option>}</For>
          </select>
        </label>
        <button class="btn-ghost text-xs"
                disabled={page() <= 1}
                onClick={() => setPage(Math.max(1, page() - 1))}>← Prev</button>
        <span class="font-mono">
          page {page()} · showing {rows.data?.length ?? 0} of ≥ {((page() - 1) * pageSize()) + (rows.data?.length ?? 0)}
        </span>
        <button class="btn-ghost text-xs"
                disabled={isLastPage()}
                onClick={() => setPage(page() + 1)}>Next →</button>
        <Show when={rows.isFetching}>
          <span class="text-ink-700">loading…</span>
        </Show>
      </div>

      <div class="flex-1 overflow-auto">
        <table class="w-full border-separate border-spacing-0 font-mono text-[11px]">
          <thead class="sticky top-0 bg-ink-100 text-[10px] uppercase tracking-wider text-ink-700">
            <tr>
              <Th>#</Th>
              <Th>When (UTC)</Th>
              <Th>Actor</Th>
              <Th>Role</Th>
              <Th>Event</Th>
              <Th>Object</Th>
              <Th>Patient</Th>
              <Th>Study</Th>
              <Th>IP</Th>
              <Th>Hash</Th>
            </tr>
          </thead>
          <tbody>
            <Show when={rows.data && rows.data.length === 0 && !rows.isLoading}>
              <tr><td colspan="10" class="p-6 text-center text-ink-700">
                No events match the current filters.
              </td></tr>
            </Show>
            <For each={rows.data ?? []}>{(r) => {
              const broken = () =>
                chain.data?.broken.includes(r.id) || chain.data?.missing_prev.includes(r.id);
              return (
                <tr class="border-t border-ink-300"
                    classList={{ "bg-danger/10": broken() }}>
                  <Td class="text-ink-700">{r.id}</Td>
                  <Td>{fmtTs(r.occurred_at)}</Td>
                  <Td class="text-white" title={r.actor_sub ?? ""}>
                    {r.metadata?.actor_name ?? (r.actor_sub ? r.actor_sub.slice(0, 12) + "…" : "system")}
                  </Td>
                  <Td class="text-ink-700">{r.actor_role ?? "—"}</Td>
                  <Td><span class="chip-brand">{r.kind}</span></Td>
                  <Td class="truncate" title={r.object_ref}>{short(r.object_ref, 32)}</Td>
                  <Td>{r.patient_mrn ?? "—"}</Td>
                  <Td class="text-ink-700" title={r.study_uid ?? ""}>
                    {r.study_uid ? short(r.study_uid, 16) : "—"}
                  </Td>
                  <Td class="text-ink-700">{r.ip ?? "—"}</Td>
                  <Td class="text-ink-700" title={`prev ${r.prev_hash}\nrow ${r.row_hash}`}>
                    {r.row_hash.slice(0, 10)}…
                  </Td>
                </tr>
              );
            }}</For>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th(p: { children: any }) {
  return <th class="border-b border-ink-300 px-3 py-2 text-left text-[10px] font-semibold">{p.children}</th>;
}
function Td(p: { children: any; class?: string; title?: string }) {
  return <td title={p.title} class={"px-3 py-2 align-middle " + (p.class ?? "")}>{p.children}</td>;
}
function Filter(p: { label: string; value: string; onInput: (s: string) => void; placeholder: string }) {
  return (
    <label class="flex flex-col gap-0.5 text-[10px] text-ink-700">
      <span>{p.label}</span>
      <input class="rounded border border-ink-300 bg-ink-200 px-2 py-1 font-mono text-[11px]"
             value={p.value} placeholder={p.placeholder}
             onInput={(e) => p.onInput(e.currentTarget.value)} />
    </label>
  );
}
function fmtTs(s: string) {
  try { return new Date(s).toISOString().replace("T", " ").replace(/\..*/, "Z"); }
  catch { return s; }
}
function short(s: string, n: number) {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

// CSV export (RFC 4180). Strings are quoted, embedded quotes doubled.
function csv(v: any): string {
  const s = v == null ? "" : String(v);
  if (/[,"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function exportCSV(rows: AuditRow[]) {
  const header = ["id","occurred_at","kind","actor_sub","actor_role","object_ref","patient_mrn","study_uid","ip","user_agent","prev_hash","row_hash","metadata"].join(",");
  const body = rows.map((r) => [
    r.id, r.occurred_at, r.kind, r.actor_sub, r.actor_role, r.object_ref,
    r.patient_mrn, r.study_uid, r.ip, r.user_agent, r.prev_hash, r.row_hash,
    JSON.stringify(r.metadata ?? {}),
  ].map(csv).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body + "\n"], { type: "text/csv" });
  downloadBlob(blob, `vaultpod-audit-${Date.now()}.csv`);
}

async function exportEvidence(rows: AuditRow[], chain: ChainStatus | null) {
  const manifest = {
    exported_at: new Date().toISOString(),
    row_count:   rows.length,
    chain_verify: chain,
    rows,
  };
  const json = JSON.stringify(manifest, null, 2);
  // Client-side SHA-256 of the JSON so the reviewer can pin a
  // specific export snapshot. The server-issued row hashes are
  // already inside `rows`; this outer hash scopes the bundle.
  const hashBuf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(json));
  const hex = Array.from(new Uint8Array(hashBuf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  const bundle = { ...manifest, bundle_sha256: hex };
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
  downloadBlob(blob, `vaultpod-audit-evidence-${Date.now()}.json`);
}

function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
